import { DialogDataModel } from './../models/dialog-data.model';
import { DialogCoreComponent } from './../modules/shared/dialogs/dialog-core/dialog-core.component';
import { Injectable, EventEmitter } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { UtilityService } from './utility.service';
import { InfoDialogComponent } from '../modules/shared/dialogs/info-dialog/info-dialog.component';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { UserObj, UserStateEnum } from '../microservice-clients/user';
import { StatsigAppService } from './statsig.service';
import { TutorContractSigningState } from '../constants';

@Injectable({
	providedIn: 'root'
})
export class PopupService {

	previousUrl: string = '';
	isModal: boolean = false;
	fullScreenModalConfig: MatDialogConfig = {
		minWidth: '100vw',
		maxHeight: '100dvh',
		position: { top: '0px' },
	}

	//TODO remove when refactor the booking flows
	onClose = new EventEmitter();

	private fullScreenDialogConfig = {
		minWidth: '100vw',
		maxWidth: '100vw'
	}
	constructor(
		private utilityService: UtilityService,
		private matDialog: MatDialog,
		private route: ActivatedRoute,
		private router: Router,
		private statsigApp: StatsigAppService,
	) { }
	async showLoginModal() {
		const { LoginDialogComponent } = await import('../standalone-components/login-or-register/login-dialog/login-dialog.component');
		this.addQueryParams('isLoginOrRegisterOpen');
		this.utilityService.trackEvent('popup', 'login', 'show');
		const dialog = this.matDialog.open(LoginDialogComponent, this.fullScreenModalConfig);
		return dialog;
	}

	async openStudentRegistration(triggeredFrom?: 'direct' | 'intro' | 'chat' | 'favourite', currentTab: number = 0, tutorName?: string) {
		const { StudentRegistrationSkeletThinComponent } = await import('../standalone-components/student-registration-thin/student-registration-skelet-thin/student-registration-skelet-thin.component');
		if (this.utilityService.isBrowser()) {
			this.statsigApp.logEvent('registration_flow_open', undefined, triggeredFrom ? { triggeredFrom } : undefined);
			const queryParams = {
				...this.route.snapshot.queryParams,
				isRegisterSkeletOpen: 'true',
				currentTab,
				...(triggeredFrom ? { triggeredFrom } : {})
			};

			this.router.navigate([], {
				relativeTo: this.route,
				queryParams,
				queryParamsHandling: 'merge'
			});
		}

		return this.matDialog.open(StudentRegistrationSkeletThinComponent, {
			...this.fullScreenModalConfig,
			closeOnNavigation: false,
			disableClose: true,
			data: { triggeredFrom, tutorName }
		});
	}

	async showRegisterModalTutor() {
		const { TutorRegisterDialogComponent } = await import('../standalone-components/login-or-register/tutor-register-dialog/tutor-register-dialog.component');
		this.addQueryParams('isLoginOrRegisterOpen');
		this.utilityService.trackEvent('popup', 'register', 'show');
		const dialog = this.matDialog.open(TutorRegisterDialogComponent, this.fullScreenDialogConfig);
		return dialog;
	}

	async checkToOpenBsnDialog(userDetails: UserObj): Promise<boolean> {
		if (
			!this.utilityService.isNL
			|| userDetails.personalInfo?.personalRegistryFirstLetters
			|| userDetails.state === UserStateEnum.Onboarding
		) {
			return false;
		}
		await this.openCompleteBsnDialog(userDetails);
		return true;
	}

	openMissingBsnDialog(userDetails: UserObj): Promise<MatDialogRef<any>> {
		if (userDetails.tutorDetails?.contractInfo?.signingState === TutorContractSigningState.Pending) {
			return this.openBsnContractDialog(userDetails);
		}
		return this.openCompleteBsnDialog(userDetails);
	}

	private async openCompleteBsnDialog(userDetails: UserObj): Promise<MatDialogRef<any>> {
		const { CompleteBsnDialogComponent } = await import('../standalone-components/complete-bsn-dialog/complete-bsn-dialog.component');
		const dialogRef = this.openResponsiveDialog(CompleteBsnDialogComponent);
		dialogRef.componentInstance.userDetailsSig.set(userDetails);
		const closeSub = this.onClose.subscribe(() => {
			dialogRef.close();
		});
		dialogRef.afterClosed().subscribe(() => {
			closeSub.unsubscribe();
		});
		return dialogRef;
	}

	private bsnContractDialogRef: MatDialogRef<any> | null = null;

	async openBsnContractDialog(userDetails: UserObj): Promise<MatDialogRef<any>> {
		if (this.bsnContractDialogRef) return this.bsnContractDialogRef;
		const { BsnContractDialogComponent } = await import('../standalone-components/bsn-contract-dialog/bsn-contract-dialog.component');
		this.bsnContractDialogRef = this.openResponsiveDialog(BsnContractDialogComponent);
		this.bsnContractDialogRef.componentInstance.userDetailsSig.set(userDetails);
		const closeSub = this.onClose.subscribe(() => {
			this.bsnContractDialogRef?.close();
		});
		this.bsnContractDialogRef.afterClosed().subscribe(() => {
			closeSub.unsubscribe();
			this.bsnContractDialogRef = null;
		});
		return this.bsnContractDialogRef;
	}

	async checkToOpenSchoolYearVerificationDialog(userDetails: UserObj) {
		const asked = userDetails?.studentDetails?.searchInterest?.asked;
		if (asked !== 'false' && asked !== false) return;
		if (userDetails.state === UserStateEnum.New || userDetails.state === UserStateEnum.Onboarding) return;

		const { SchoolYearVerificationDialogComponent } = await import('../standalone-components/school-year-verification-dialog/school-year-verification-dialog.component');
		this.openResponsiveDialog(SchoolYearVerificationDialogComponent, {
			disableClose: true,
			closeOnNavigation: false,
		});
	}

	openResponsiveDialog(dialogComp, config?: MatDialogConfig, skipAnimation: boolean = false): MatDialogRef<any> {
		const configuration: MatDialogConfig = {
			maxWidth: "100vw",
			minWidth: config?.minWidth ? config.minWidth : "",
			maxHeight: config?.maxHeight ? config.maxHeight : "95vh",
			enterAnimationDuration: (this.utilityService.isDesktopRes() || skipAnimation) ? 200 : 0,
			exitAnimationDuration: (this.utilityService.isDesktopRes() || skipAnimation) ? 200 : 0,
			panelClass: config?.panelClass,
			disableClose: true,
			closeOnNavigation: config?.closeOnNavigation ?? true,
			...(config?.autoFocus !== undefined && { autoFocus: config.autoFocus }),
		}
		const dialogRef = this.matDialog.open(dialogComp, configuration);
		if (!skipAnimation) {
			this.addAnimationToDialog(dialogRef)
		}
		return dialogRef;
	}

	openInfoDialog(data: DialogDataModel) {
		const dialog = this.openResponsiveDialog(DialogCoreComponent, { panelClass: ['default-dialog'], disableClose: true });
		const _component = dialog.componentInstance;
		_component.title = data.title;
		_component.childComponent = InfoDialogComponent;
		_component.onChildReady.subscribe((childInstance: InfoDialogComponent) => {
			childInstance.description = data.description ?? ''
		})

		if (data.trackEvent) {
			this.trackDialogEvent(data.trackEvent, dialog)
		}
		return dialog;
	}

	private addAnimationToDialog(dialogRef: MatDialogRef<any>) {
		const dialogContainer = dialogRef.componentRef.location.nativeElement.firstElementChild
		if (!this.utilityService.isMobileRes()) {
			return
		}
		dialogContainer.animate([
			{ transform: 'translateY(100%)' },
			{ transform: 'translateY(0%)' },
		], {
			duration: 200,
			easing: 'ease-out'
		});
		const originalCloseMethod = dialogRef.close
		dialogRef.close = () => {
			const animation = dialogContainer.animate([
				{ transform: 'translateY(0)' },
				{ transform: 'translateY(100%)' }
			], {
				duration: 200,
				easing: 'ease-in',
				fill: 'forwards'
			});
			animation.onfinish = () => {
				dialogRef.close = originalCloseMethod;
				dialogRef.close();
			};
		}
	}

	private trackDialogEvent(trackEvent: string, dialog: MatDialogRef<any>) {
		this.utilityService.trackEvent('popup', trackEvent, 'show')
		dialog.afterClosed().subscribe(() => {
			this.utilityService.trackEvent('popup', trackEvent, 'hide')
		})
	}

	private addQueryParams(queryParamName: string) {
		const queryParams = this.route.snapshot.queryParams;
		if (!queryParams[queryParamName]) {
			this.utilityService.addOpenQueryParam(queryParamName, this.route, this.router);
		}
		this.previousUrl = this.router.url;
	}
}